// Thin fetch wrapper around the backend API.
//
// Phase 1: hand-written. Once `make codegen-web` produces src/api/generated/,
// migrate callers to the typed client and delete this file.

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8080';

export class APIError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

type FetchOpts = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
};

async function request<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const access = sessionStore.getAccess();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers ?? {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
  let res = await fetch(API_BASE + path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    credentials: 'include',
  });

  // On 401, try a refresh once.
  if (res.status === 401 && access) {
    const ok = await tryRefresh();
    if (ok) {
      const newAccess = sessionStore.getAccess();
      if (newAccess) headers.Authorization = `Bearer ${newAccess}`;
      res = await fetch(API_BASE + path, {
        method: opts.method ?? 'GET',
        headers,
        body: opts.body ? JSON.stringify(opts.body) : undefined,
        credentials: 'include',
      });
    }
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    // Terminal-401 path: we ended up with a 401 AND have no session left
    // (refresh failed + cleared, OR no access token to begin with).
    // Bounce to sign-in so the user doesn't see a generic 'forbidden'
    // toast on whatever button they just clicked.
    if (res.status === 401 && !sessionStore.getAccess()) {
      redirectToSignin();
    }
    const err = (data?.error ?? {}) as { code?: string; message?: string; details?: unknown };
    throw new APIError(res.status, err.code ?? 'UNKNOWN', err.message ?? 'Request failed', err.details);
  }
  return data as T;
}

// Bounce the user to /auth/signin from anywhere in the lib — used when
// the refresh path returns 401 (session is unambiguously dead). Uses
// window.location.assign so the redirect works from non-React contexts
// (api.ts is pure module code). Guard against re-redirecting when the
// user is already on an /auth/ page so we don't loop.
function redirectToSignin() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/auth/')) return;
  // Stash the path so a future "return to" flow could honour it; left
  // here as a hook point — current SignIn just bounces to /home.
  try { sessionStorage.setItem('wydm.returnTo', window.location.pathname + window.location.search); } catch { /* noop */ }
  window.location.assign('/auth/signin');
}

// Singleton in-flight refresh promise — every concurrent 401 awaits the
// same call instead of POSTing /auth/refresh in parallel. Without this,
// the 30-second notification poll + any foreground request both fire
// independent refresh calls when the access token expires; the first
// rotates the refresh token, the second arrives with the now-revoked
// original, the backend's reuse-detection kills the whole family +
// signs the user out. See plan §25.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const refresh = sessionStore.getRefresh();
    if (!refresh) return false;
    try {
      const res = await fetch(API_BASE + '/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      });
      if (!res.ok) {
        // Only nuke the session on a definitive 401 (refresh token is
        // unambiguously invalid). 5xx / network errors / cors hiccups
        // are transient — let the next request retry instead of forcing
        // a sign-in. This used to clear on ANY failure, which turned
        // every deploy-bounce and network blip into a forced re-login.
        if (res.status === 401) {
          sessionStore.clear();
          // Send the user straight to sign-in so a follow-up action
          // doesn't surface a generic "forbidden" alert.
          redirectToSignin();
        }
        return false;
      }
      const data = await res.json();
      sessionStore.set(data.access, data.refresh, data.user);
      return true;
    } catch {
      // Network error — keep tokens, next call retries.
      return false;
    }
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

// ── session store (localStorage backed; httpOnly cookies would be cleaner
//    but require a same-origin proxy at the edge — TBD before launch) ────
export interface User {
  id: string;
  email: string;
  display_name?: string;
  dob?: string; // YYYY-MM-DD; omitted until profile_complete
  tier: 'free' | 'premium';
  // Granular plan kind. Optional — backend may not send on older
  // sessions; absence treated as 'monthly' (the historical default)
  // when premium, 'none' when free. The UI uses this for display
  // (Lifetime badge etc.); the security gate is the binary `tier`.
  plan?: 'none' | 'monthly' | 'yearly' | 'life';
  profile_complete: boolean;
  created_at: string;
  // Set when the user has cancelled their auto-renewing sub via
  // the App Store / Play Store but is still inside the paid period.
  // PlanBadge surfaces a "Cancels on <date>" annotation when true.
  // Omitted when there's no active sub OR when the sub is renewing
  // normally — healthy subs don't broadcast their renewal date.
  subscription_cancelled?: boolean;
  // ISO date string — the period-end of the cancelled sub. Only
  // populated when subscription_cancelled is true.
  subscription_expires_at?: string;
  // ISO date string — the period-end of a recently-expired sub
  // (within the last 30 days). Lets the Free PlanBadge surface
  // a "Plan ended <date>" pill so the user can confirm their
  // cancellation took effect when they expected. Absent for
  // never-subscribed users or once the 30-day grace lapses.
  subscription_ended_at?: string;
  // ISO date string — the next renewal date for a healthy
  // (active, not cancelled) recurring sub. PlanBadge subtitle
  // surfaces this as "Renews on <date>." so the user knows
  // their next billing date. Mutually exclusive with
  // subscription_expires_at (cancelled subs use that one).
  // Absent for lifetime + for free.
  subscription_renews_at?: string;
}

const KEY_ACCESS  = 'wydm.session.access';
const KEY_REFRESH = 'wydm.session.refresh';
const KEY_USER    = 'wydm.session.user';

export const sessionStore = {
  getAccess(): string | null { return localStorage.getItem(KEY_ACCESS); },
  getRefresh(): string | null { return localStorage.getItem(KEY_REFRESH); },
  getUser(): User | null {
    const raw = localStorage.getItem(KEY_USER);
    return raw ? (JSON.parse(raw) as User) : null;
  },
  set(access: string, refresh: string, user: User) {
    localStorage.setItem(KEY_ACCESS, access);
    localStorage.setItem(KEY_REFRESH, refresh);
    localStorage.setItem(KEY_USER, JSON.stringify(user));
  },
  clear() {
    localStorage.removeItem(KEY_ACCESS);
    localStorage.removeItem(KEY_REFRESH);
    localStorage.removeItem(KEY_USER);
  },
};

// ── typed API surface ───────────────────────────────────────────────────

export const api = {
  signup: (body: { email: string; password: string; terms_accepted: boolean }) =>
    request<TokenPair>('/auth/signup', { method: 'POST', body }),
  signin: (body: { email: string; password: string }) =>
    request<TokenPair>('/auth/signin', { method: 'POST', body }),
  oauthApple: (body: { id_token: string; terms_accepted?: boolean }) =>
    request<TokenPair>('/auth/oauth/apple', { method: 'POST', body }),
  oauthGoogle: (body: { id_token: string; terms_accepted?: boolean }) =>
    request<TokenPair>('/auth/oauth/google', { method: 'POST', body }),
  // Pre-launch waitlist capture. Public, unauthenticated. Used by the
  // "WYDM isn't accepting sign-ups yet" modal when signup endpoints
  // 403 with SIGNUPS_DISABLED. Idempotent on email.
  waitlist: (body: { email: string; source?: string }) =>
    request<{ ok: true }>('/auth/waitlist', { method: 'POST', body }),
  me: () => request<User>('/auth/me'),
  // Soft-delete the caller's account. 30-day grace before hard purge;
  // active subscriptions auto-cancel at period end. Backend revokes
  // all sessions immediately, so the client should clear local
  // storage + bounce to landing after this resolves.
  deleteAccount: (body: { reason?: string }) =>
    request<{ ok: true; deleted_at: string; hard_delete_after: string }>(
      '/auth/account/delete',
      { method: 'POST', body },
    ),
  // GDPR Art. 20 data export. Returns a Blob the caller triggers as
  // a file download — not the request() wrapper because the response
  // is binary, not JSON. Rate-limited 3/day per user; 429 surfaces
  // here as an APIError.
  async downloadDataExport(): Promise<Blob> {
    const access = sessionStore.getAccess();
    const res = await fetch(API_BASE + '/auth/account/data-export', {
      headers: access ? { Authorization: 'Bearer ' + access } : {},
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
      throw new APIError(
        res.status,
        body?.error?.code ?? 'EXPORT_FAILED',
        body?.error?.message ?? `Server responded with ${res.status}`,
      );
    }
    return res.blob();
  },
  // Both fields are optional — backend ignores empties + only updates
  // what's provided. Use for signup completion (both set) AND for
  // in-builder edits to either field.
  completeProfile: (body: { dob?: string; display_name?: string }) =>
    request<User>('/auth/profile/complete', { method: 'POST', body }),
  signout: () => request<void>('/auth/signout', { method: 'POST', body: { refresh: sessionStore.getRefresh() ?? '' } }),
  passwordResetRequest: (email: string) =>
    request<{ ok: boolean }>('/auth/password-reset/request', { method: 'POST', body: { email } }),
  passwordResetConfirm: (body: { email: string; code: string; new_password: string }) =>
    request<{ ok: boolean }>('/auth/password-reset/confirm', { method: 'POST', body }),
};

export interface TokenPair {
  access: string;
  refresh: string;
  expires_at: string;
  user: User;
}

// ── profile types ──────────────────────────────────────────────────────────

export type Gender = 'woman' | 'man';
export type LookingFor = 'men' | 'women' | 'everyone';
export type Visibility = 'private' | 'link_only' | 'public';

export interface Profile {
  id: string;
  user_id: string;
  display_name?: string;
  bio?: string;
  gender?: Gender;
  looking_for: LookingFor[];
  slug?: string;
  visibility: Visibility;
  published: boolean;
  published_at?: string;
  public_url?: string;
  created_at: string;
  updated_at: string;
}

export interface PublishResp {
  slug: string;
  public_url: string;
  profile: Profile;
}

// Patch payload — only present fields are written. Use `null`/empty string
// to clear a field explicitly.
export interface ProfilePatch {
  bio?: string;
  gender?: Gender | '';
  looking_for?: LookingFor[];
}

export const profilesApi = {
  me: () => request<Profile>('/profiles/me'),
  patch: (body: ProfilePatch) =>
    request<Profile>('/profiles/me', { method: 'PATCH', body }),
  publish: () => request<PublishResp>('/profiles/publish', { method: 'POST' }),
  setVisibility: (visibility: Visibility) =>
    request<Profile>('/profiles/me/visibility', { method: 'PATCH', body: { visibility } }),
  listMyMedia: () => request<MediaItem[]>('/profiles/me/media'),
  reorderMedia: (media_ids: string[]) =>
    request<MediaItem[]>('/profiles/me/media-order', { method: 'PUT', body: { media_ids } }),
};

// ── public (auth-free) profile read ────────────────────────────────────────

// PublicProfile is the slim, privacy-safe shape the backend returns from
// GET /public/profiles/:slug. No user_id, no dob, no internal timestamps.
export interface PublicPhoto {
  position: number;
  url: string;
}

export interface SpotifyTrack {
  id: string;
  name: string;
  artist: string;
  album?: string;
  image_url?: string;
  preview_url?: string;
  external_url?: string;
}

export interface SpotifyArtist {
  id: string;
  name: string;
  image_url?: string;
  external_url?: string;
  genres?: string[];
}

export interface PublicProfile {
  slug: string;
  display_name: string;
  age?: number;
  gender?: Gender;
  looking_for: LookingFor[];
  bio?: string;
  photos: PublicPhoto[];
  music: SpotifyTrack[];
  music_artists: SpotifyArtist[];
  // Ratings stats — populated in M3. For now backend returns nulls.
  rating_count?: number | null;
  yes_percent?: number | null;
}

// ── spotify (owner-scoped) ─────────────────────────────────────────────────

export interface SpotifyConnection {
  connected: boolean;
  display_name?: string;
  tracks: SpotifyTrack[];
  artists: SpotifyArtist[];
  tracks_fetched_at?: string;
}

export const spotifyApi = {
  // Returns { authorize_url } — UI window.location's to it.
  connect: () => request<{ authorize_url: string }>('/integrations/spotify/connect'),
  // me({ force: true }) forces a refetch of top tracks + artists from
  // Spotify, bypassing the 24h cache. Refresh button calls this.
  me: (opts?: { force?: boolean }) =>
    request<SpotifyConnection>(
      '/integrations/spotify/me' + (opts?.force ? '?force=true' : ''),
    ),
  disconnect: () => request<void>('/integrations/spotify/me', { method: 'DELETE' }),
};

export const publicApi = {
  getProfile: (slug: string) =>
    request<PublicProfile>(`/public/profiles/${encodeURIComponent(slug)}`),
  // note is optional, 240-char max. Trimmed server-side. Empty/omitted
  // leaves any existing note untouched on re-rate. captcha_token is
  // required when the deployed backend has HCAPTCHA_SECRET_KEY set
  // (prod); ignored in dev where the verifier is disabled.
  rate: (slug: string, body: { verdict: 'yes' | 'no'; fingerprint: string; note?: string; captcha_token?: string }) =>
    request<RateResp>(`/public/profiles/${encodeURIComponent(slug)}/rate`, {
      method: 'POST',
      body,
    }),
  // Report an abusive profile. Anonymous-friendly — when the caller is
  // signed in the auth header is included via the standard request()
  // wrapper and the backend records reporter_user_id. Otherwise it
  // logs an anonymous report keyed by IP.
  report: (slug: string, body: { classification: ReportClassification; comment: string }) =>
    request<{ ok: true }>(`/public/profiles/${encodeURIComponent(slug)}/report`, {
      method: 'POST',
      body,
    }),
};

export type ReportClassification =
  | 'inappropriate_photos'
  | 'fake_profile'
  | 'harassment'
  | 'spam'
  | 'underage'
  | 'other';

// Label order for the report-modal radio list. Order = display order.
export const REPORT_CLASSIFICATIONS: { id: ReportClassification; label: string; hint?: string }[] = [
  { id: 'inappropriate_photos', label: 'Inappropriate photos', hint: 'Nudity, sexual content' },
  { id: 'fake_profile',         label: 'Fake profile', hint: 'Stolen photos, catfish' },
  { id: 'harassment',           label: 'Harassment or offensive content' },
  { id: 'spam',                 label: 'Spam or scam' },
  { id: 'underage',             label: 'Looks under 18' },
  { id: 'other',                label: 'Other', hint: 'Add details below' },
];

export const REPORT_COMMENT_MAX = 1000;

// Canonical plan ordering — monthly → yearly → lifetime, then any
// unrecognised shape at the end. The backend returns plans in whatever
// order Stripe + RC hand them back, which isn't stable; we sort
// client-side so the UI is predictable regardless of vendor wobble.
const PLAN_RANK: Record<string, number> = {
  monthly:  0,
  yearly:   1,
  lifetime: 2,
};

function planRank(p: BillingPlan): number {
  if (p.kind === 'one_time') return PLAN_RANK.lifetime;
  if (p.interval === 'month') return PLAN_RANK.monthly;
  if (p.interval === 'year') return PLAN_RANK.yearly;
  return 99; // unknown shapes (eg. weekly) sort last
}

/** Sort billing plans into our canonical display order. Pure; safe to
 *  call on the array straight from the API. Preserves input order
 *  within the same rank bucket (matters when Stripe has multiple
 *  monthly prices, eg. one USD + one GBP). */
export function sortPlans(plans: BillingPlan[]): BillingPlan[] {
  return [...plans].sort((a, b) => planRank(a) - planRank(b));
}

// Public CTA enforces the same limit client-side; backend has its own
// CHECK so a hand-crafted POST can't sneak past either way.
export const RATING_NOTE_MAX = 240;

// hCaptcha site key exposed to the public web. Safe to ship in the
// bundle (the secret key never leaves the backend). Empty = the rate
// widget renders nothing; backend skips verification in matching dev
// environments. To enable: set HCAPTCHA_SITE_KEY in CI's web-build env.
export const HCAPTCHA_SITE_KEY: string = (import.meta.env.VITE_HCAPTCHA_SITE_KEY as string | undefined) ?? '';

// ── ratings (owner-scoped + public submit response) ────────────────────────

export interface RateResp {
  verdict: 'yes' | 'no';
  your_verdict: 'yes' | 'no';
  rating_count: number;
  yes_percent: number;
}

export interface RatingsDashboard {
  rating_count: number;
  yes_count: number;
  no_count: number;
  yes_percent: number;
  recent: { id: string; verdict: 'yes' | 'no'; note?: string; created_at: string }[];
}

// Period scopes the ratings dashboard + the stats share card to a
// rolling time window. 'today' is the last 24 hours (no calendar-day
// rollover, no TZ math), 'week' the last 7 days, 'all' is open.
// Default 'all'.
export type Period = 'today' | 'week' | 'all';

export const ratingsApi = {
  dashboard: (period: Period = 'all') =>
    request<RatingsDashboard>(
      `/profiles/me/ratings${period !== 'all' ? `?period=${period}` : ''}`,
    ),
};

// ── media types ────────────────────────────────────────────────────────────

export type MediaState =
  | 'uploading'
  | 'scanning_nsfw'
  | 'published'
  | 'rejected_nsfw'
  | 'csam_match';

export interface MediaItem {
  id: string;
  position: number;
  url: string;
  state: MediaState;
  rejected_reason?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  created_at: string;
}

export interface PresignResp {
  media_id: string;
  upload_url: string;
  key: string;
  expires_at: string;
}

export const mediaApi = {
  presign: (content_type: string, position?: number) =>
    request<PresignResp>('/media/presign', {
      method: 'POST',
      body: { content_type, position },
    }),
  finalize: (media_id: string) =>
    request<MediaItem>('/media/finalize', { method: 'POST', body: { media_id } }),
  delete: (id: string) => request<void>(`/media/${id}`, { method: 'DELETE' }),

  // Same-origin URL that proxies the given (R2-hosted) media URL. Used
  // by the share-card capture so the <img> + fetch are CORS-safe and
  // the canvas isn't tainted. The backend validates that the URL host
  // matches R2_PUBLIC_BASE_URL before fetching, so this isn't an open
  // proxy.
  proxyURL: (url: string): string =>
    `${API_BASE}/media/proxy?url=${encodeURIComponent(url)}`,
};

/**
 * Full upload helper — handles the three-step dance:
 *   1. Ask backend for a presigned PUT URL + media row
 *   2. PUT bytes directly to R2 (the backend never sees the bytes)
 *   3. POST /media/finalize so the backend HEADs R2 + flips state to published
 *
 * Returns the final MediaItem (in `published` state for M2.2; in
 * `scanning_nsfw` for M2.3+).
 */
export async function uploadPhoto(file: File, position?: number): Promise<MediaItem> {
  if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
    throw new APIError(400, 'UNSUPPORTED_TYPE', 'Photos must be JPEG, PNG, or WebP.');
  }
  const presigned = await mediaApi.presign(file.type, position);
  const put = await fetch(presigned.upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!put.ok) {
    throw new APIError(put.status, 'R2_UPLOAD_FAILED', 'Photo upload to storage failed.');
  }
  return mediaApi.finalize(presigned.media_id);
}

export const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// ── notifications + push (M4.A) ────────────────────────────────────────────

export type NotificationEvent =
  | 'rating.received'
  | 'rating.with_note'
  | 'ai_review.ready'
  | 'dm.message'
  | 'media.processed'
  | 'media.rejected';

export interface NotificationItem {
  id: string;
  event_type: NotificationEvent;
  title: string;
  body: string;
  link?: string;
  read_at?: string;
  created_at: string;
}

export interface InboxResponse {
  items: NotificationItem[];
  // Total unread across every category — kept for back-compat.
  unread_count: number;
  // Split by category so the messages tab can show one badge and the
  // notifications bell another, both from the same poll.
  unread_dm_count: number;
  unread_other_count: number;
  next_cursor?: string;
}

export type NotificationCategory = 'dm' | 'other' | 'all';

export interface NotificationPreferences {
  rating_received: boolean;
  rating_with_note: boolean;
  ai_review_ready: boolean;
  dm_message: boolean;
  media_processed: boolean;
  media_rejected: boolean;
  payment_received: boolean;
  payment_failed: boolean;
  subscription_cancelled: boolean;
  subscription_expired: boolean;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  quiet_hours_tz?: string | null;
}

export interface RegisterTokenBody {
  platform: 'ios' | 'android' | 'web';
  token: string;
  web_endpoint?: string;
  web_p256dh?: string;
  web_auth?: string;
  device_name?: string;
  app_version?: string;
}

export interface RegisteredToken {
  id: string;
  platform: string;
  device_name?: string;
}

export const notificationsApi = {
  // `category` filters the items array (dm only / non-dm / all) but
  // never the unread counts — those breakdown numbers always come
  // back so a single poll cycle can populate both the bell + the
  // messages-tab badges.
  list: (opts: { before?: string; category?: NotificationCategory } = {}) => {
    const params = new URLSearchParams();
    if (opts.before) params.set('before', opts.before);
    if (opts.category && opts.category !== 'all') params.set('category', opts.category);
    const qs = params.toString();
    return request<InboxResponse>('/notifications' + (qs ? '?' + qs : ''));
  },
  markRead: (id: string) =>
    request<void>(`/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: () =>
    request<void>('/notifications/read-all', { method: 'POST' }),

  getPrefs: () => request<NotificationPreferences>('/notifications/preferences'),
  updatePrefs: (prefs: NotificationPreferences) =>
    request<NotificationPreferences>('/notifications/preferences', {
      method: 'PUT',
      body: prefs,
    }),

  registerToken: (body: RegisterTokenBody) =>
    request<RegisteredToken>('/notifications/tokens', { method: 'POST', body }),
  unregisterToken: (id: string) =>
    request<void>(`/notifications/tokens/${id}`, { method: 'DELETE' }),
};

// ── AI reviews (M4.B) ──────────────────────────────────────────────────────

export type AIPersonality = 'honest_friend' | 'brutal_critic' | 'hype_man';

export interface AIPersonalityMeta {
  id: AIPersonality;
  display_name: string;
  tagline: string;
}

export type AIReviewStatus = 'pending' | 'streaming' | 'complete' | 'failed';

export interface AIReviewListEntry {
  id: string;
  personality: AIPersonality;
  status: AIReviewStatus;
  provider?: string;
  model?: string;
  created_at: string;
  completed_at?: string;
}

export interface AIReviewDetail {
  id: string;
  personality: AIPersonality;
  status: AIReviewStatus;
  body: string;
  provider?: string;
  model?: string;
  tokens_in: number;
  tokens_out: number;
  error?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  profile_version_at: string;
}

export interface AIPersonalityEligibility {
  id: AIPersonality;
  can_generate: boolean;
  reason?: 'ok' | 'in_flight' | 'profile_unchanged' | 'unknown';
  last_review_id?: string;
  last_review_at?: string;
}

export interface AIEligibility {
  profile_version_at: string;
  in_flight_review_id?: string;
  personalities: AIPersonalityEligibility[];
}

export const aireviewApi = {
  personalities: () =>
    request<{ personalities: AIPersonalityMeta[] }>('/ai/personalities'),
  // Picker's one-shot mount call: per-personality CTA state + the
  // global in-flight gate + current profile_version. Replaces the
  // older pattern of calling list() and scraping it client-side.
  eligibility: () => request<AIEligibility>('/ai/eligibility'),
  list: () =>
    request<{ items: AIReviewListEntry[] }>('/ai/reviews'),
  get: (id: string) =>
    request<AIReviewDetail>(`/ai/reviews/${id}`),
  create: (personality: AIPersonality) =>
    request<{ id: string; personality: AIPersonality; status: AIReviewStatus }>(
      '/ai/reviews',
      { method: 'POST', body: { personality } },
    ),
  // Resolves "what's my current review for personality X" → {id}.
  // Used by /ai/latest/<personality> deep-link routes (from
  // notifications). 404 when nothing exists for that personality.
  latestByPersonality: (personality: string) =>
    request<{ id: string }>(`/ai/personalities/${encodeURIComponent(personality)}/latest`),
  // No `remove` — DELETE endpoint was retired to close the "delete +
  // re-roll" loophole that bypassed the profile-staleness gate.
  // Streaming URL — caller passes this to openSSE() from lib/sse.ts.
  // Returned as a relative path so the SSE helper prepends API_BASE.
  streamPath: (id: string) => `/ai/reviews/${id}/stream`,
};

// ── billing (M4.C) ──────────────────────────────────────────────────────
//
// Vendor-loaded plans: backend pulls them at runtime from Stripe +
// RevenueCat at /billing/plans, so the client is dumb — it renders what
// it's given and routes the upgrade tap by the `vendor` tag.

export type BillingVendor = 'stripe' | 'revenuecat';

export interface BillingPlan {
  vendor: BillingVendor;
  vendor_price_id: string;
  name: string;
  description?: string;
  unit_amount: number;
  currency: string;
  // 'recurring' (monthly/yearly Stripe Price) or 'one_time' (lifetime).
  // Drives the Checkout mode + UI: one_time renders a 'Lifetime ·
  // One-time' chip instead of '/month'.
  kind: 'recurring' | 'one_time';
  interval?: string;
  interval_count?: number;
}

export const billingApi = {
  plans: (opts: { refresh?: boolean } = {}) =>
    request<{ plans: BillingPlan[] }>(
      `/billing/plans${opts.refresh ? '?refresh=1' : ''}`,
    ),
  // Stripe Hosted Checkout — returns a checkout_url the browser
  // redirects to. Server-side session, no Stripe.js needed on the
  // client.
  checkout: (vendor_price_id: string) =>
    request<{ checkout_url: string }>('/billing/checkout', {
      method: 'POST',
      body: { vendor_price_id },
    }),
  // Stripe Customer Portal — returns a portal_url for managing the
  // subscription. 404s with NO_SUBSCRIPTION if the user has never paid.
  portal: () => request<{ portal_url: string }>('/billing/portal'),
};

// ── DMs (M5) ────────────────────────────────────────────────────────────

export interface DMThreadParty {
  user_id: string;
  display_name: string;
  avatar_url?: string;
  // Profile slug for the public-profile link from the thread header.
  // Backend allows DM partners to view even private profiles
  // (block-aware) via this slug.
  slug?: string;
}

export interface DMMessage {
  id: string;
  thread_id: string;
  sender_id: string;
  body: string;
  read_at?: string | null;
  created_at: string;
}

export interface DMThread {
  id: string;
  other_user: DMThreadParty;
  last_message?: DMMessage;
  unread_count: number;
  initiator_user_id: string;
  blocked_by_user_id?: string;
  created_at: string;
  last_message_at: string;
}

// DMInboxSummary is the free-tier metadata-only inbox shape. Backend
// returns it instead of full threads when the caller is free, so the
// UI can render the "X new messages from Y people waiting" paywall
// pitch without ever seeing sender names or message bodies. Premium
// callers get the full `threads` array and these fields stay absent.
export interface DMInboxSummary {
  thread_count: number;
  unread_count: number;
  sender_count: number;
}

export interface DMInboxResponse {
  threads: DMThread[];
  premium_required?: boolean;
  summary?: DMInboxSummary;
}

export const dmApi = {
  listThreads: () =>
    request<DMInboxResponse>('/dm/threads'),
  getThread: (id: string) =>
    request<DMThread>(`/dm/threads/${id}`),
  listMessages: (id: string, before?: string) =>
    request<{ messages: DMMessage[] }>(
      '/dm/threads/' + id + '/messages' + (before ? '?before=' + encodeURIComponent(before) : ''),
    ),
  // Create or fetch a thread for a recipient. Pass either their
  // user_id (when known — eg. from the inbox) or their public slug
  // (when initiating from /p/:slug — the public DTO doesn't expose
  // user_id, so the backend resolves slug → user_id server-side).
  // Errors: 402 PREMIUM_REQUIRED, 403 NOT_RATED / BLOCKED,
  // 400 SELF_DM, 404 RECIPIENT_GONE.
  createThread: (body: { recipient_user_id?: string; recipient_slug?: string }) =>
    request<DMThread>('/dm/threads', { method: 'POST', body }),
  send: (id: string, body: string) =>
    request<DMMessage>('/dm/threads/' + id + '/messages', { method: 'POST', body: { body } }),
  markRead: (id: string, up_to_message_id: string) =>
    request<{ updated: number }>('/dm/threads/' + id + '/read', {
      method: 'POST', body: { up_to_message_id },
    }),
  block: (id: string) =>
    request<void>('/dm/threads/' + id + '/block', { method: 'POST' }),
  unblock: (id: string) =>
    request<void>('/dm/threads/' + id + '/block', { method: 'DELETE' }),
  report: (message_id: string, reason: string) =>
    request<{ id: string }>('/dm/reports', { method: 'POST', body: { message_id, reason } }),
  // WebSocket path. Caller passes this to openDMSocket() from lib/dmws.ts
  // which prepends ws://... and appends ?access_token=...
  wsPath: () => '/dm/ws',
};
